import { useEffect, useRef } from 'react';
import { GameSocket, ConnectOptions } from './client';
import { useGameStore } from '../store';

/**
 * Establishes a single GameSocket for the component's lifetime, funnels events
 * into the store, and returns the socket for sending actions. Reconnects are
 * handled by GameSocket; the store's `connected` flag reflects live status.
 */
export function useGameSocket(opts: ConnectOptions | null): GameSocket | null {
  const socketRef = useRef<GameSocket | null>(null);
  const { apply, setConnected, reset } = useGameStore.getState();

  // Key the effect on the meaningful identity of opts.
  const key = opts ? `${opts.role}:${opts.gameId ?? ''}:${opts.token ?? ''}` : null;

  useEffect(() => {
    if (!opts) return;
    reset();
    const socket = new GameSocket(opts);
    socketRef.current = socket;
    const offMsg = socket.onMessage(apply);
    const offStatus = socket.onStatus(setConnected);
    void socket.connect();
    return () => {
      offMsg();
      offStatus();
      socket.close();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return socketRef.current;
}
